<template>
  <div v-if="noDialog">
    <template v-if="success">
      <v-card>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col class="text-center mt-8">
                <v-icon x-large color="success">
                  mdi-check-circle-outline
                </v-icon>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="text-center mb-4">
                <h3>{{ $t("success.smsSended") }}</h3>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </template>
    <v-form @submit.prevent="sendSMS" ref="form" lazy-validation v-else>
      <v-card>
        <v-card-title>Send SMS</v-card-title>
        <v-card-text>
          <SMSFields
            :receiverItems="receiverItems"
            :SMSFieldsData.sync="SMSFieldsData"
          ></SMSFields>
        </v-card-text>
        <v-card-actions class="d-flex justify-center pb-4">
          <v-btn type="submit" color="success" :loading="loading">
            {{ $t("common.action.send") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </div>
  <div v-else>
    <v-tooltip bottom :disabled="!tooltip">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          :icon="!fab"
          :fab="fab"
          :color="outlined ? undefined : color"
          :outlined="outlined"
          :class="{ 'btn-outline-accent': outlined }"
          :loading="loading"
          @click.stop="openSMSFormDialog"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon>mdi-cellphone-text</v-icon>
        </v-btn>
      </template>
      <span>{{ tooltip }}</span>
    </v-tooltip>
    <v-dialog v-model="SMSFormDialog" width="auto">
      <template v-if="success">
        <v-card>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col class="text-center mt-8">
                  <v-icon x-large color="success">
                    mdi-check-circle-outline
                  </v-icon>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="text-center mb-4">
                  <h3>{{ $t("success.smsSended") }}</h3>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </template>
      <v-form @submit.prevent="sendSMS" ref="form" lazy-validation v-else>
        <v-card>
          <v-card-title>Send SMS</v-card-title>
          <v-card-text>
            <SMSFields
              :receiverItems="receiverItems"
              :SMSFieldsData.sync="SMSFieldsData"
            ></SMSFields>
          </v-card-text>
          <v-card-actions class="d-flex justify-center pb-4">
            <v-btn text @click="SMSFormDialog = false">
              {{ $t("common.action.cancel") }}
            </v-btn>
            <v-btn type="submit" color="success" :loading="loading">
              {{ $t("common.action.send") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import Component from "vue-class-component";
import Vue from "vue";
import { Prop } from "vue-property-decorator";
import SMSFields from "./SMSFields.vue";
import { SMSFieldsData } from "@/api/models/sms/sms-fields-data";
import { plainToInstance } from "class-transformer";
import { VForm } from "@/utils/VForm.type";
import { ReceiverItem } from "@/api/models/sms/type/receiver-item.interface";

@Component({
  components: {
    SMSFields,
  },
})
export default class SMSActivator extends Vue {
  @Prop({ type: Function, required: true })
  public readonly sendSMSFunction!: (
    receivers: string[],
    message: string
  ) => Promise<void>;

  @Prop({ type: Boolean, default: false })
  public readonly fab!: boolean;

  @Prop({ type: Boolean, default: false })
  public readonly outlined!: boolean;

  @Prop({ type: String, required: false })
  public readonly tooltip!: string;

  @Prop({ type: Boolean, default: false })
  public readonly noDialog!: boolean;

  @Prop({ type: String, default: "primary" })
  public readonly color!: string;

  @Prop({ type: String, default: "en" })
  public readonly lang!: string;

  @Prop({ type: Array, required: false })
  public readonly receiverItems?: ReceiverItem[];

  public loading = false;
  public success = false;
  public SMSFormDialog = false;
  public SMSFieldsData: SMSFieldsData | null = null;

  public openSMSFormDialog() {
    this.SMSFieldsData = plainToInstance(SMSFieldsData, {
      receivers: [...(this.receiverItems ?? [])],
      message: "",
      signature: this.$tc("sms.defaultSignature", 0, this.lang),
    });
    this.SMSFormDialog = true;
  }

  public async sendSMS() {
    if (this.SMSFieldsData === null) {
      return;
    }

    this.loading = true;

    const form = this.$refs.form as VForm;
    if (form?.validate()) {
      const receivers = this.SMSFieldsData.receivers.map((receiver) =>
        typeof receiver === "string" ? receiver : receiver.value
      );
      try {
        await this.sendSMSFunction(
          receivers,
          `${this.SMSFieldsData.message} - ${this.SMSFieldsData.signature}`
        );
        this.success = true;
        setTimeout(() => {
          this.success = false;
          this.SMSFormDialog = false;
        }, 2000);
      } catch (err) {
        //
      }
    } else {
      const input = form.inputs.find((input) => !input.valid);
      if (
        input &&
        typeof input.focus === "function" &&
        input.$el !== undefined
      ) {
        this.$vuetify.goTo(input.$el, { offset: 50 });
        input.focus();
      }
    }

    this.loading = false;
  }
}
</script>
